/**
 * The pool screen's own top row: a back arrow, the pool's name, and a share
 * button, left to right, and nothing else (ISSUE-952 R2).
 *
 * `AppShell` renders no header on this screen, so this row is the whole top
 * of the viewport. Exactly three children, in that order, is the contract.
 *
 * `flex-wrap` is load-bearing, not decoration, for the same reason the shell
 * header carries it: the theme fixes text sizes in px but keeps spacing in
 * rem, so raising the root font grows every gap and pad in this row while the
 * labels stay as wide as they were. Without wrapping, a long pool name at the
 * largest text scale pushes the document past a 390px viewport (R12).
 */
import { Button } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { POOL_BACK_LABEL } from "./poolScreenCopy";
import { PoolShareButton } from "./PoolShareButton";

export type typePoolScreenHeaderProps = {
  poolId: string;
  poolName: string;
  viewerPoolRole: "owner" | "admin" | "member";
  onGoBack: () => void;
};

const BackGlyph = (): ReactNode => (
  <svg
    data-testid="pool-back-icon"
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 24 24"
    className="h-icon-md w-icon-md"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 5 8 12 15 19" />
  </svg>
);

/**
 * The back arrow on its own.
 *
 * The shell renders no header on this screen, so this control is the fan's
 * only way out of a pool that is still loading or whose read failed. Those
 * phases have no pool name and no role, so they cannot draw the full header
 * row, and before this existed a failed `POST /pool/get` left a retry banner
 * with no navigation anywhere in the document.
 */
export const PoolScreenBackButton = ({
  onGoBack,
}: {
  onGoBack: () => void;
}): ReactNode => (
  <Button
    variant="ghost"
    size="icon"
    aria-label={POOL_BACK_LABEL}
    data-testid="pool-screen-back"
    onClick={onGoBack}
  >
    <BackGlyph />
  </Button>
);

export const PoolScreenHeader = ({
  poolId,
  poolName,
  viewerPoolRole,
  onGoBack,
}: typePoolScreenHeaderProps): ReactNode => (
  <div
    className="flex flex-wrap items-center gap-2"
    data-testid="pool-screen-header"
  >
    <PoolScreenBackButton onGoBack={onGoBack} />
    <h1
      className="min-w-0 flex-1 truncate text-h1 text-ink"
      data-testid="pool-screen-title"
    >
      {poolName}
    </h1>
    <PoolShareButton
      poolId={poolId}
      poolName={poolName}
      viewerPoolRole={viewerPoolRole}
    />
  </div>
);
